import { lddGlobalSessionInfo } from "../navigationTypes";
import KeyDate from "./KeyDate";
import { useEffect, useState, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { RPWDataReader } from "../../livedataFuncs";
import styles from "./ProgressBar.module.scss";
import { DEFAULT_RADIUS, ProgressBarElements, ProgressBarProfiles } from "../../../../libs/taskmanagement/src/ProgressBarConfig";
import { TaskPreferenceItem, TKeyDate } from "../../../../libs/taskmanagement/src/taskTypes";
import React from "react";
import { Popover, OverlayTrigger, Row, Col } from "react-bootstrap";
import { setProgressBarTask } from "../../redux/reducer";
import { runInnerFunction } from "../../compatManager";

export interface ProgressBarControllerProps {
    sessionInfo: lddGlobalSessionInfo;
    DataReaderObj: RPWDataReader;
}

export interface ProfileConfig {
    title: string;
    size: number;
    index: number;
    tasks?: any[];
    includeAllExcept?: Record<number, string[]>;
} 
export interface ProgressBarConfig {
    fileTypes: string[];
    profiles: Record<string, ProfileConfig>;
    dateMarkers: number[];
    hasPostClosing: boolean;
}

export interface ProgressBarProps {
    profileName: string;
    profileData: ProfileConfig;
    currentDate: Date;
    preferences: TaskPreferenceItem[];
    isLast: boolean;
}

const getTaskDate = (dateStr: string) => {
    const [year, month, day] = dateStr?.split('-').map(Number);
    const taskDate = new Date(year, month - 1, day);
    return taskDate;
}

const ProgressBarDetailsPopover: React.FC<{
    tasksRef: React.MutableRefObject<any[]>;
    profileName: string;
    currentDate: Date;
    preferences: TaskPreferenceItem[];
}> = ({ tasksRef, profileName, currentDate, preferences }) => {
    const dispatch = useAppDispatch();
    const completedCount = tasksRef.current.filter(task => task.isCompleted === 1).length;
    const overdueCount = tasksRef.current.filter(
        task => task.isCompleted !== 1 && 
        getTaskDate(task.date) < currentDate
    ).length;
    const taskCount = tasksRef.current.length;

    const handleStatusClick = (task: any) => {
        const taskIndex = tasksRef.current.findIndex(t => t === task);
        if (taskIndex !== -1) {
            const updatedTask = {
                ...tasksRef.current[taskIndex],
                isCompleted: task.isCompleted === 1 ? 0 : 1
            };
            tasksRef.current[taskIndex] = updatedTask;
            dispatch(setProgressBarTask(updatedTask));
            const event = new CustomEvent('taskStatusChanged', { detail: { task: updatedTask } });
            window.dispatchEvent(event);
            runInnerFunction("SetPageDirty", null);
        }
    };

    return (
        <>
            <Popover.Header>
                <h3>{profileName}</h3>
                <div>
                    <span className={styles.total}><b className={styles.completed}>{completedCount}</b> / {taskCount} tasks</span>
                    <span className={styles.overdue}>
                        • {overdueCount} overdue
                    </span>
                </div>
            </Popover.Header>

            <Popover.Body>
                {tasksRef.current
                    .slice()
                    .sort((a, b) => getTaskDate(a.date) >= getTaskDate(b.date) ? 1 : -1)
                    .map((task, idx) => {
                        const preference = preferences.find(pref => parseInt(pref.typeID) === task.typeID);
                        const cleanTitle = task.title ? task.title.replace(/[()]/g, '') : '';
                        const displayName = preference ? preference.description : task.matterNumber || cleanTitle;

                        let icon;
                        let flag;
                        if (task.isCompleted === 1) {
                            icon = (
                                <i 
                                    className={`fa-solid fa-circle-check status-success ${styles.taskIcon}`}
                                    onClick={() => handleStatusClick(task)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            );
                        } else if (getTaskDate(task.date) < currentDate) {
                            icon = (
                                <i 
                                    className={`fa-regular fa-circle-check status-secondary-light ${styles.taskIcon}`}
                                    onClick={() => handleStatusClick(task)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            );
                            flag = <i className={`fa-solid fa-flag-swallowtail text-danger ${styles.taskIcon}`}></i>;
                        } else {
                            icon = (
                                <i 
                                    className={`fa-regular fa-circle-check status-secondary-light ${styles.taskIcon}`}
                                    onClick={() => handleStatusClick(task)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            );
                        }
                        return (
                            <Row className="mb-1 gx-2" key={idx}>
                                <Col className="col-md-auto">{icon}</Col>
                                <Col className="text-truncate col">{displayName}</Col>
                                <Col className="align-right d-flex align-items-center col-md-auto">{flag}</Col>
                            </Row>
                        );
                    })}
            </Popover.Body>
        </>
    );
};

const ProgressBar = ({ profileName, profileData, currentDate, preferences, isLast }: ProgressBarProps) => {
    const tasksRef = useRef(profileData.tasks || []);
    const [showPopover, setShowPopover] = useState(false);
    const [, forceUpdate] = useState({});
    const taskCount = tasksRef.current.length;
    const completedCount = tasksRef.current.filter(task => task.isCompleted === 1).length;
    const isDueBefore = (dateStr: string, compareDate: Date) => {
        if (!dateStr) 
            return false;
        const d = getTaskDate(dateStr);
        return d < compareDate;
    };
    const getNetOverdueTasksCount = (currentDate: Date) => {
        const overDueCount = tasksRef.current.filter(task => isDueBefore(task.date, currentDate) && task.isCompleted !== 1).length;
        const completedCount = tasksRef.current.filter(task => task.isCompleted === 1).length;
        return overDueCount + completedCount;
    }
    const orangeCount = Math.min(getNetOverdueTasksCount(currentDate), taskCount);
    const greenPercent = (completedCount / (taskCount || 1)) * 100;
    const orangePercent = (orangeCount / (taskCount || 1)) * 100;
    const greenRadius = DEFAULT_RADIUS;
    const greenBarWidth = completedCount > 0 ? `${greenPercent}%` : '0%';

    useEffect(() => {
        tasksRef.current = profileData?.tasks || [];
        forceUpdate({});
    }, [profileData?.tasks]);
    
    useEffect(() => {
        const handleTaskStatusChange = () => {
            forceUpdate({});
        };

        window.addEventListener('taskStatusChanged', handleTaskStatusChange);
        return () => {
            window.removeEventListener('taskStatusChanged', handleTaskStatusChange);
        };
    }, []);

    return (
        <OverlayTrigger
            show={showPopover}
            onToggle={(nextShow) => setShowPopover(nextShow)}
            trigger={[]}
            placement="bottom"
            delay={{ show: 0, hide: 200 }}
            overlay={
                <Popover 
                    id={`popover-${profileName}`} 
                    className={styles.popover}
                    onMouseEnter={() => setShowPopover(true)}
                    onMouseLeave={() => setShowPopover(false)}
                >
                    <ProgressBarDetailsPopover
                        tasksRef={tasksRef}
                        profileName={profileName}
                        currentDate={currentDate}
                        preferences={preferences}
                    />
                </Popover>
            }
        >
            <div
                className={styles.progressBarItem}
                style={{ width: `${profileData.size * 100}%`, position: 'relative' }}
                onMouseEnter={() => setShowPopover(true)}
                onMouseLeave={() => setShowPopover(false)}
            >
                {orangeCount > 0 && (
                    <div
                        className={styles.barOrange}
                        style={{ width: `${orangePercent}%`, borderRadius: DEFAULT_RADIUS }}
                    >
                        {completedCount === 0 && orangeCount > 0 && (
                            <span className={styles.barText}>{`0/${taskCount} tasks`}</span>
                        )}
                    </div>
                )}
                {
                    orangeCount === 0 && completedCount === 0 && (
                        taskCount === 0 ? (
                            <span className={styles.barNoDateText}>No Pending Tasks</span>
                        ) : (
                            <span className={styles.barTextDark}>{`0/${taskCount} tasks`}</span>
                        )
                    )
                }
                <div
                    className={`${styles.barGreen}`}
                    style={{
                        width: greenBarWidth,
                        minWidth: '65px',
                        borderRadius: greenRadius,
                        justifyContent: 'flex-end',
                        paddingLeft: 0,
                        paddingRight: completedCount && completedCount !== taskCount ? 6 : 0,
                        opacity: completedCount > 0 ? 1 : 0,
                        pointerEvents: completedCount > 0 ? 'auto' : 'none',
                    }}
                >
                    <span className={styles.barText}>{`${completedCount}/${taskCount} tasks`}</span>
                </div>
            </div>
        </OverlayTrigger>
    );
};

function addTasksForMatchingPreferences(profileData: ProfileConfig, matchingPreferences: TaskPreferenceItem[], activeTasks: any[]) {
    matchingPreferences.forEach(preference => {
        const preferenceTypeID = parseInt(preference.typeID);
        const tasksForThisPreference = activeTasks.filter(
            task => task.typeID === preferenceTypeID && 
            (!task.overwriteDate || task.overwriteDate === 0)
        );
        tasksForThisPreference.forEach(task => {
            if (!profileData.tasks?.some(existingTask => existingTask.typeID === task.typeID)) {
                profileData.tasks?.push(task);
            }
        });
    });
    return profileData;
}

export const ProgressBarController = (props: ProgressBarControllerProps) => {
    const { sessionInfo, DataReaderObj } = props;
    const psm = DataReaderObj?.getPSM();
    const province = sessionInfo?.appProvinceShort;
    const tasks = useAppSelector(state => state.InFileTasks);
    const taskPreferences = useAppSelector(state => state.TaskPreferences);
    const [progressBar, setProgressBar] = useState<ProgressBarConfig | null>(null);
    const [preferences, setPreferences] = useState<TaskPreferenceItem[]>([]);
    const fileClosingDate = DataReaderObj?.getClosingDate();
    const closingDate = fileClosingDate ? getTaskDate(fileClosingDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const prepareProgressBarConfig = () => {
        const provinceConfig = ProgressBarElements[province];
        const fileTypeConfig = provinceConfig.find(config => config.fileTypes.includes(psm)) || provinceConfig[0];
        return fileTypeConfig as ProgressBarConfig;
    }

    const getFileKeyDates = (preferences: TaskPreferenceItem[]): TKeyDate[] => {
        const keyDates = KeyDate.KeyDatesDefine.dates.map(keyDateDefinition => ({
            ...(keyDateDefinition as TKeyDate),
            id: keyDateDefinition.id,
            title: (keyDateDefinition.title === 'Possession' && province === 'SK') ? 'Closing' : keyDateDefinition.title,
            psm: psm,
            date: DataReaderObj && keyDateDefinition.dateFunc ? DataReaderObj[keyDateDefinition.dateFunc]() : null
        }));        
        const filteredKeyDates = keyDates.filter(keyDate => {
            if (keyDate.date === null) {return false;}
            return preferences.some(preference => preference.CalendarItemDate === keyDate.title);
        });
        return filteredKeyDates;
    }

    const keepActiveTasks = () => {
        return tasks.filter(task => task.isActivated === 1);
    }

    const keepPreferencesActiveTasks = (activeTasks: any[]) => {
        return taskPreferences.filter(preference => activeTasks.some(task => task.typeID === parseInt(preference.typeID)));
    }

    const isValidPreferenceToInclude = (preference: TaskPreferenceItem, profileData: ProfileConfig, beforeAfterIndicators: string[], keyDateTitle: string) => {
        let isValid = true;
        if(profileData.includeAllExcept){
            for (const [key, value] of Object.entries(profileData.includeAllExcept)) {
                if ((key === preference.CalendarItemDate && value.includes(preference.beforeAfter))) {
                    isValid = false;
                    break;
                } 
            }
        }
        else{
            isValid = preference.CalendarItemDate === keyDateTitle && (beforeAfterIndicators.includes(preference.beforeAfter) || (beforeAfterIndicators.includes('on') && !preference.beforeAfter));
        }
        return isValid;
    }

    const filterTasksByKeyDate = (progressBarConfig: Record<string, ProfileConfig>, fileKeyDates: TKeyDate[], preferencesActiveTasks: TaskPreferenceItem[], activeTasks: any[]) => {
        const newProgressBarConfig = { ...progressBarConfig };

        Object.keys(newProgressBarConfig).forEach((profileName, index) => {
            if (Object.prototype.hasOwnProperty.call(newProgressBarConfig, profileName)) {
                let profileData = newProgressBarConfig[profileName];
                profileData.tasks = [];
                const profileRules = ProgressBarProfiles[profileName as keyof typeof ProgressBarProfiles];

                if (profileRules) {
                    for (const keyDateIdStr in profileRules) {
                        const keyDateIdNum = parseInt(keyDateIdStr);
                        if (Object.prototype.hasOwnProperty.call(profileRules, keyDateIdStr)) {
                            const beforeAfterIndicators: string[] = profileRules[keyDateIdNum].beforeAfter;
                            const relevantKeyDate = fileKeyDates.find(kd => kd.id === keyDateIdNum);
                            if (relevantKeyDate && beforeAfterIndicators && beforeAfterIndicators.length > 0) {
                                const keyDateTitle = relevantKeyDate.title;
                                const matchingPreferences = preferencesActiveTasks.filter(preference => 
                                    isValidPreferenceToInclude(preference, profileData, beforeAfterIndicators, keyDateTitle)
                                );
                                profileData = addTasksForMatchingPreferences(profileData, matchingPreferences, activeTasks);
                            }
                        }
                        if(profileData.includeAllExcept){
                            if(profileData.tasks.length > 0){
                            break;
                            } else if(keyDateIdNum === 1 && index < Object.keys(newProgressBarConfig).length - 1){
                            for (const fileKeyDate of fileKeyDates) {
                                const innerKeyDateIdNum = fileKeyDate.id;
                                if (innerKeyDateIdNum !== 11 && innerKeyDateIdNum !== 1) {
                                    const keyDateTitle = fileKeyDate.title;
                                    const matchingPreferences = preferencesActiveTasks.filter(preference => 
                                        isValidPreferenceToInclude(preference, profileData, [], keyDateTitle)
                                    );
                                    profileData = addTasksForMatchingPreferences(profileData, matchingPreferences, activeTasks);
                                }
                            }
                                break;
                            }
                        }
                    }
                    if (Object.keys(profileRules).map(Number).includes(1)) {
                        const tasksWithOverwriteDate = activeTasks.filter(task => task.overwriteDate === 1);

                        tasksWithOverwriteDate.forEach(task => {
                            const taskDate = getTaskDate(task?.date);

                            if ((!closingDate || closingDate >= taskDate) && (index < Object.keys(newProgressBarConfig).length - 1)) {
                                profileData.tasks.push(task);
                            } else if (closingDate && closingDate < taskDate && index === Object.keys(newProgressBarConfig).length - 1) {
                                profileData.tasks.push(task);
                            }
                        });
                    }
                }
            }
        });
        return newProgressBarConfig;
    }        
    
    useEffect(() => {
        const config: ProgressBarConfig = prepareProgressBarConfig();
        const activeTasks = keepActiveTasks();
        const preferencesActiveTasks = keepPreferencesActiveTasks(activeTasks);
        const fileKeyDates = getFileKeyDates(preferencesActiveTasks);
        config.profiles = filterTasksByKeyDate(config?.profiles, fileKeyDates, preferencesActiveTasks, activeTasks);
        setProgressBar(config);
        setPreferences(preferencesActiveTasks);
    }, [tasks, taskPreferences]);

    return progressBar ? (
        <div className={styles.progressBarContainer}>
            {Object.entries(progressBar.profiles)
                .sort(([, a], [, b]) => a.index - b.index)
                .map(([profileName, profileData]) => (
                    <ProgressBar
                        key={profileName}
                        profileName={profileData.title}
                        profileData={profileData}
                        currentDate={today}
                        preferences={preferences}
                        isLast={profileData.index === Object.keys(progressBar.profiles).length - 1}
                    />
                ))}
        </div>
    ) : null;
};

export default ProgressBarController;
